import { useEffect, useMemo } from 'react'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

import { MobileModal } from 'components/UI/Modal/MobileModal'
import FbButton from 'components/FbUI/FbButton'
import useApiService from 'services/api'
import { ReminderCreatePayload, ReminderType } from 'models/reminders'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { DatePicker } from 'components/FormUtils/DatePicker'
import TimePicker from 'components/FormUtils/TimePicker'
import { useAuth } from 'context/authentication/useAuth'

const QUICK_VALUES = ['1_week', '2_weeks', '1_month', '3_months'] as const
type QuickSchedule = (typeof QUICK_VALUES)[number]

const QUICK_LABEL: Record<QuickSchedule, string> = {
  '1_week': '1 week',
  '2_weeks': '2 weeks',
  '1_month': '1 month',
  '3_months': '3 months',
}

const reminderSchema = z
  .object({
    title: z
      .string()
      .max(50, 'Title must be 50 characters or less')
      .refine((v) => !!v, { message: 'Title is required' }),
    run_at_date: z.string().optional(),
    run_at_time: z.string().optional(),
    quick_schedule: z
      .union([
        z.literal('1_week'),
        z.literal('2_weeks'),
        z.literal('1_month'),
        z.literal('3_months'),
      ])
      .optional()
      .nullable(),
    quick_choice: z
      .union([
        z.literal('1_week'),
        z.literal('2_weeks'),
        z.literal('1_month'),
        z.literal('3_months'),
      ])
      .optional()
      .nullable(),
    frequency: z
      .union([
        z.literal('one_off'),
        z.literal('daily'),
        z.literal('weekly'),
        z.literal('monthly'),
      ])
      .default('one_off'),
    recipients: z
      .array(z.number())
      .min(1, 'You must select at least one recipient'),
    content: z.string().optional().nullable(),
  })
  .refine(
    (v) => {
      // Either quick_schedule OR a date must be provided
      return !!v.quick_schedule || !!v.run_at_date
    },
    {
      message: 'Select a date or use a quick schedule option',
      path: ['run_at_date'],
    }
  )

type ReminderFormValues = z.infer<typeof reminderSchema>

type MobileCreateReminderPageProps = {
  contactCompanyId?: number
  doorProxyId?: number
  dealIds?: number[]
}

function mapFrequencyToType(
  freq: ReminderFormValues['frequency']
): ReminderType {
  switch (freq) {
    case 'daily':
      return 'daily'
    case 'weekly':
      return 'weekly'
    case 'monthly':
      return 'monthly'
    case 'one_off':
    default:
      return 'one_off'
  }
}

export function MobileCreateReminderPage({
  contactCompanyId,
  doorProxyId,
  dealIds,
}: MobileCreateReminderPageProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()

  const methods = useForm<ReminderFormValues>({
    resolver: zodResolver(reminderSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      run_at_date: '',
      run_at_time: '09:00',
      quick_schedule: null,
      quick_choice: null,
      frequency: 'one_off',
      recipients: user?.id ? [user.id] : [],
      content: '',
    },
  })

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isSubmitting, isValid, errors },
  } = methods

  const quickSchedule = watch('quick_schedule')
  const quickChoice = watch('quick_choice')
  const runAtDate = watch('run_at_date')
  const runAtTime = watch('run_at_time') || '09:00'
  const recipients = watch('recipients')

  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  const runAt = useMemo(() => {
    if (!runAtDate) return undefined
    return new Date(`${runAtDate}T${runAtTime}:00`)
  }, [runAtDate, runAtTime])

  const dateTimeError = useMemo(() => {
    if (!runAtDate || quickSchedule) return null
    const selected = new Date(`${runAtDate}T${runAtTime}:00`)
    const now = new Date()
    if (selected <= now) {
      return 'Please select a future date and time for the reminder'
    }
    return null
  }, [runAtDate, runAtTime, quickSchedule])

  // Users for recipient select
  const { data: userOptions } = api.useGetUserOptions({ onlyActive: true })
  const recipientOptions =
    userOptions?.map((u: any) => ({
      label: u.label,
      value: u.value,
    })) ?? []

  const frequencyOptions = [
    { label: 'Once', value: 'one_off' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
  ]

  const mutation = useMutation({
    mutationFn: async (values: ReminderFormValues) => {
      let run_at: string | undefined
      if (values.quick_schedule) {
        // backend handles quick_schedule + no run_at
      } else if (runAt) {
        run_at = runAt.toISOString()
      }

      const payload: ReminderCreatePayload = {
        title: values.title,
        content: values.content || '',
        recipients: values.recipients,
        contact_company: contactCompanyId,
        door_proxy: doorProxyId,
        deals: dealIds,
        run_at,
        quick_schedule: values.quick_schedule ?? undefined,
        reminder_type: mapFrequencyToType(values.frequency),
      }

      return api.createReminder(payload)
    },
    onSuccess: () => {
      toast.success('Reminder scheduled')
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
      navigate(-1)
    },
    onError: () => {
      toast.error('Failed to schedule reminder')
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
  })

  const handleBack = () => {
    navigate(-1)
  }

  const handleQuickScheduleClick = (value: QuickSchedule) => {
    const today = new Date()

    const target = new Date(today)
    switch (value) {
      case '1_week':
        target.setDate(today.getDate() + 7)
        break
      case '2_weeks':
        target.setDate(today.getDate() + 14)
        break
      case '1_month':
        target.setMonth(today.getMonth() + 1)
        break
      case '3_months':
        target.setMonth(today.getMonth() + 3)
        break
    }

    const year = target.getFullYear()
    const month = String(target.getMonth() + 1).padStart(2, '0')
    const day = String(target.getDate()).padStart(2, '0')
    const formatted = `${year}-${month}-${day}`

    // set both actual date and quick_schedule, and mark active for styling
    setValue('run_at_date', formatted, { shouldValidate: true })
    setValue('quick_schedule', value, { shouldValidate: true })
    setValue('quick_choice', value, { shouldValidate: false })
  }

  return (
    <MobileModal
      handleBack={handleBack}
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Create a Reminder
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Add Details</h1>
          <p className="mt-1 text-xs text-gray-500">
            Reminder emails will be sent at the selected time to the selected
            recipients, and can be viewed or edited on the Activity page.
          </p>
        </div>
      }
      footer={
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="secondary"
              onClick={handleBack}
            >
              Cancel
            </FbButton>
          </div>
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="primary"
              onClick={onSubmit}
              disabled={
                !isValid ||
                isSubmitting ||
                recipients.length === 0 ||
                !!dateTimeError
              }
            >
              Schedule Reminder
            </FbButton>
          </div>
        </div>
      }
    >
      <FormProvider {...methods}>
        <div className="flex flex-col gap-4 px-4 pb-4 pt-2">
          {/* Title */}
          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Title*
            </label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                         placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
              placeholder="Reminder title"
              {...register('title')}
            />
            {errors.title && (
              <p className="mt-1 text-xs text-red-600">
                {String(errors.title.message)}
              </p>
            )}
          </div>

          {/* Date + quick schedule using DatePicker */}
          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Date*
            </label>
            <Controller
              control={control}
              name="run_at_date"
              render={({ field }) => (
                <DatePicker
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val)
                    // manual date clears quick flags
                    setValue('quick_schedule', null, { shouldValidate: true })
                    setValue('quick_choice', null, { shouldValidate: false })
                  }}
                  className="w-full"
                />
              )}
            />
            <div className="grid grid-cols-4 gap-2 mt-3">
              {QUICK_VALUES.map((key) => {
                const isActive = quickChoice === key
                return (
                  <button
                    key={key}
                    type="button"
                    className={[
                      'px-2 py-1.5 text-xs border rounded w-full',
                      isActive
                        ? 'border-emerald-700 bg-emerald-50 text-emerald-800'
                        : 'border-gray-300 bg-white text-gray-700',
                    ].join(' ')}
                    onClick={() => handleQuickScheduleClick(key)}
                  >
                    {QUICK_LABEL[key]}
                  </button>
                )
              })}
            </div>
            {errors.run_at_date && !dateTimeError && (
              <p className="mt-1 text-xs text-red-600">
                {String(errors.run_at_date.message)}
              </p>
            )}
          </div>

          {/* Time (optional) using TimePicker */}
          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Time{' '}
              <span className="italic text-sm font-normal">- optional</span>
            </label>
            <Controller
              control={control}
              name="run_at_time"
              render={({ field }) => (
                <TimePicker
                  className="w-full"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Frequency */}
          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Frequency
            </label>
            <Controller
              control={control}
              name="frequency"
              render={({ field }) => (
                <ReactSelectWrapper
                  placeholder="Select frequency"
                  options={frequencyOptions}
                  value={
                    frequencyOptions.find((o) => o.value === field.value) ||
                    null
                  }
                  onChange={(opt: any) => field.onChange(opt?.value)}
                  isClearable={false}
                />
              )}
            />
          </div>

          {/* Recipients (multi select) */}
          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Recipient(s)*
            </label>
            <Controller
              control={control}
              name="recipients"
              render={({ field }) => (
                <ReactSelectWrapper
                  isMulti
                  placeholder="Select recipients..."
                  value={recipientOptions.filter((u: any) =>
                    field.value?.includes(u.value)
                  )}
                  options={recipientOptions as any}
                  onChange={(v) =>
                    field.onChange((v as any[]).map((i) => (i as any).value))
                  }
                />
              )}
            />
            {errors.recipients && (
              <p className="mt-1 text-xs text-red-600">
                {String(errors.recipients.message)}
              </p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="text-base font-medium text-gray-800 mb-1">
              Content
              <span className="italic text-sm font-normal"> - optional</span>
            </label>
            <textarea
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm
                         placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-fb-green-600"
              placeholder="Reminder details..."
              {...register('content')}
            />
          </div>

          {/* Date/time validation error */}
          {dateTimeError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-red-600 text-xs flex items-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {dateTimeError}
              </p>
            </div>
          )}
        </div>
      </FormProvider>
    </MobileModal>
  )
}
